import { useEffect, useState } from "react";
import {
  formatHexRgbLabel,
  tryParseColorString,
} from "@/document/model/colorString";
import {
  hsbToOklch,
  hslToOklch,
  oklchColor,
  rgbToOklch,
  round,
  type HsbColor,
  type HslColor,
  type OklchColor,
  type RgbColor,
} from "@/document/model/color";
import { NumberField } from "./NumberField";
import { ScrubField } from "./ScrubField";
import { ScrubInput } from "./ScrubInput";

export function HexEditor({ color, onChange }: { color: OklchColor; onChange: (color: OklchColor) => void }) {
  const [draft, setDraft] = useState(formatHexRgbLabel(color));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) {
      setDraft(formatHexRgbLabel(color));
    }
  }, [color, editing]);

  function commit(value: string): void {
    const parsed = tryParseColorString(value);
    if (parsed) {
      onChange({ ...parsed, alpha: color.alpha });
      setDraft(formatHexRgbLabel(parsed));
      return;
    }

    setDraft(formatHexRgbLabel(color));
  }

  return (
    <ScrubField label="HEX" scrubbable={false}>
      <ScrubInput
        value={draft}
        onFocus={() => setEditing(true)}
        onChange={event => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          setEditing(false);
          commit(draft);
        }}
        onKeyDown={event => {
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            setDraft(formatHexRgbLabel(color));
            setEditing(false);
            event.currentTarget.blur();
          }
        }}
        className="font-mono"
      />
    </ScrubField>
  );
}

export function RgbEditor({ color, onChange }: { color: RgbColor; onChange: (color: RgbColor) => void }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <NumberField label="R" value={color.r} min={0} max={255} onCommit={r => onChange({ ...color, r })} />
      <NumberField label="G" value={color.g} min={0} max={255} onCommit={g => onChange({ ...color, g })} />
      <NumberField label="B" value={color.b} min={0} max={255} onCommit={b => onChange({ ...color, b })} />
    </div>
  );
}

export function HslEditor({ color, onChange }: { color: HslColor; onChange: (color: HslColor) => void }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <NumberField label="H" value={round(color.h)} min={0} max={360} onCommit={h => onChange({ ...color, h })} />
      <NumberField label="S" value={round(color.s * 100)} min={0} max={100} onCommit={s => onChange({ ...color, s: s / 100 })} />
      <NumberField label="L" value={round(color.l * 100)} min={0} max={100} onCommit={l => onChange({ ...color, l: l / 100 })} />
    </div>
  );
}

export function HsbEditor({ color, onChange }: { color: HsbColor; onChange: (color: HsbColor) => void }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <NumberField label="H" value={round(color.h)} min={0} max={360} onCommit={h => onChange({ ...color, h })} />
      <NumberField label="S" value={round(color.s * 100)} min={0} max={100} onCommit={s => onChange({ ...color, s: s / 100 })} />
      <NumberField label="B" value={round(color.b * 100)} min={0} max={100} onCommit={b => onChange({ ...color, b: b / 100 })} />
    </div>
  );
}

export function OklchEditor({ color, onChange }: { color: OklchColor; onChange: (color: OklchColor) => void }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <NumberField label="L" value={round(color.l * 100)} min={0} max={100} onCommit={l => onChange(oklchColor(l / 100, color.c, color.h, color.alpha))} />
      <NumberField label="C" value={round(color.c, 3)} min={0} max={0.5} step={0.001} onCommit={c => onChange(oklchColor(color.l, c, color.h, color.alpha))} />
      <NumberField label="H" value={round(color.h)} min={0} max={360} onCommit={h => onChange(oklchColor(color.l, color.c, h, color.alpha))} />
    </div>
  );
}
